import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { UserPreview } from '../interfaces/user-preview.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { Platform } from '@ionic/angular';
// import OAuth2Config from 'src/app/shared/authentication/OAuth2Config';
import { map } from 'rxjs/operators';
import { TokenStorageService, BackendService, BaseAuthService } from 'projects/auth/src/lib/services';
import { isNil } from 'lodash';
import { UserRoleEnum } from '../enums/UserRoleEnum';
import { ConfigService } from './multi-clients/config.service';
import { setDefaultStyles } from './external-client.service';
import { getOAuth2Config } from '../authentication/OAuth2Config';
import { BrandingService } from './branding.service';
import * as _ from 'lodash';
import { SSO_SUCCESS_KEY } from 'auth/src/lib/constants';

export class Result<T> {
    public data: T;
    public success: boolean;
    public errors: string[];
}

export class LoginResponse {
    public access_token: string;
    public expires_in: number;
    public refresh_token: string;
    public name: string;
    public Name: string;
    public username: string;
    public imageUrl: string;
    public user_id: string;
    public UserId: string;
    public role: string;
    public completed_registration: boolean;
    public first_access: boolean;
    public email_verified: boolean;
    public adminAccesses?: Array<string>;
    public have_dependents?: boolean;
    public roles: UserRoleEnum[];
    public sso: boolean;
    public country?: string;
}

export class UserItem {
    public name: string;
    public username: string;
    public manager: boolean;
    public imageUrl: string;
    public lineManager: UserItem;
    public id: string;
    public email?: string;
    public group?: string;
    public rank?: string;
    public tokenNeedsRefresh?: boolean;
}

export class LoginOAuthResponse
{
    access_token: string; // antigo
    refresh_token: string; // antigo
    access_token_response:
    {
        access_token: string;
        expires_in: number;
        ext_expires_in: number;
        scope: string;
        token_type: string;
    }
}

@Injectable()
export class AuthService {

    public personificationChanged$ = new EventEmitter<boolean>();
    public apiUrl: any;

    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private passwordChangeRequiredSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private isLoginSubject: BehaviorSubject<boolean>;

    private logoutUrl;
    private logoutByClientEnabled;

    private localStorageKey = 'auth_data';
    private passwordChangeRequiredKey = 'passwordChangeRequired';
    private redirectUrlKey = 'redirect_url';
    private loggingOutKey = 'loggingOut';


    constructor(private _httpService: BackendService,
        private router: Router,
        private _httpClient: HttpClient,
        private _platform: Platform,
        private _tokenService: TokenStorageService,
        private _baseAuthService: BaseAuthService,
        private _configService: ConfigService,
        private _tokenStorage: TokenStorageService,
        private _brandingService: BrandingService,
    ) {
        const hasAuthData = this.hasToken();
        this.isLoginSubject = new BehaviorSubject<boolean>(hasAuthData);

        const passwordStorageItem = localStorage.getItem(this.passwordChangeRequiredKey);
        this.setPasswordChangeRequired(passwordStorageItem ? passwordStorageItem === 'true' : false);
    }

    private setEnvironmentVariables() {
        this.logoutUrl = this._configService.settings.features.sso.logoutUrl;
        this.logoutByClientEnabled = this._configService.settings.features.sso.logoutByClient;
        this.apiUrl = this._configService.settings.apiUrl;
    }

    public async login(username: string, password: string): Promise<any> {
        this.refreshTokenSubject.next(null);
        let result: any = { success: false };
        try {
            result =
                await this._httpService.post<LoginResponse>('login', {
                    'UserName': username,
                    'Password': password
                }).toPromise();
            this.setPasswordChangeRequired(result.data.passwordChangeRequired);
        } catch (err) {
            if (err.error) {
                result = err.error;
            }
        }
        if (result.success) {
            localStorage.setItem(this.localStorageKey, JSON.stringify({
                'access_token': 'Bearer ' + result.data.tokenResource.accessToken,
                'username': result.data.tokenResource.username,
                'user_id': result.data.tokenResource.userId,
                'name': result.data.name,
                'roles': result.data.roles,
                'country': result.data.country,
            }));
            this._tokenService.saveToken({
                'access_token': 'Bearer ' + result.data.tokenResource.accessToken,
                'username': result.data.tokenResource.username,
                'user_id': result.data.tokenResource.userId,
                'name': result.data.name,
                'roles': result.data.roles,
                'country': result.data.country,
            });
            const authData = result.data;
            const token = authData.tokenResource.accessToken;
            this.refreshTokenSubject.next(token);
            this.isLoginSubject.next(true);
        }
        return result;
    }

    public async ssoLogin(tokenResource) {
        localStorage.setItem(this.localStorageKey, JSON.stringify({
            'access_token': 'Bearer ' + tokenResource.AccessToken,
            'username': tokenResource.Username,
            'user_id': tokenResource.UserId,
            'name': tokenResource.Name,
            'roles': tokenResource.Roles ? tokenResource.Roles.split(',') : ''
        }));
        this._tokenService.saveToken({
            'access_token': 'Bearer ' + tokenResource.AccessToken,
            'username': tokenResource.Username,
            'user_id': tokenResource.UserId,
            'name': tokenResource.Name,
            'roles': tokenResource.Roles ? tokenResource.Roles.split(',') : ''
        });
        this.isLoginSubject.next(true);
    }

    public async updateTokenAndRoles(tokenResource): Promise<void> {
        // Obtém dados atuais do usuário
        const currentUser = this.getLoggedUser();
        
        // Atualiza apenas token e roles, mantém o restante
        const updatedUser = {
            'access_token': 'Bearer ' + tokenResource.AccessToken,
            'username': currentUser.username,
            'user_id': currentUser.user_id,
            'name': currentUser.name,
            'roles': tokenResource.Roles ? tokenResource.Roles.split(',') : [],
            'country': currentUser.country
        };
        
        localStorage.setItem(this.localStorageKey, JSON.stringify(updatedUser));
        this._tokenService.saveToken(updatedUser);
        this.isLoginSubject.next(true);
    }

    public async loginOAuthSSO(): Promise<any> {
        if (!this._platform.is('capacitor')) {
            throw new Error('OAuth SSO is only available on mobile devices');
        }

        return OAuth2Client.authenticate(getOAuth2Config(this._configService)).then(async (response) => {
            const tokenResponse = await this.loginOAuth(response).toPromise();
            const tokenResource = tokenResponse.data;
            localStorage.setItem(this.localStorageKey, JSON.stringify({
                'access_token': 'Bearer ' + tokenResource.accessToken,
                'username': tokenResource.username,
                'user_id': tokenResource.userId,
                'name': tokenResource.name,
                'roles': tokenResource.roles ? tokenResource.roles.split(',') : ''
            }));
            this._tokenService.saveToken({
                'access_token': 'Bearer ' + tokenResource.accessToken,
                'username': tokenResource.username,
                'user_id': tokenResource.userId,
                'name': tokenResource.name,
                'roles': tokenResource.roles ? tokenResource.roles.split(',') : ''
            });
            this.isLoginSubject.next(true);
        });
    }

    public async setLoggedUser(loggedUser: LoginResponse): Promise<any> {
        localStorage.setItem(this.localStorageKey, JSON.stringify({
            'access_token': 'Bearer ' + loggedUser.access_token,
            'username': loggedUser.username,
            'user_id': loggedUser.user_id,
            'name': loggedUser.name,
            'roles': loggedUser.roles ? loggedUser.roles : '',
            'country': loggedUser.country,
        }));
    }

    private loginOAuth(data: LoginOAuthResponse): Observable<any> {
        this.setEnvironmentVariables();
        const token = _.isNull(data.access_token) || _.isEmpty(data.access_token) ? data.access_token_response?.access_token : data.access_token;
        const headers = new HttpHeaders({
            'Authorization': 'Bearer ' + token
        });

        return this._httpClient.post(`${this.apiUrl}/api/account/login/oauth`, data, { headers });
    }

    public changePassword(newPassword: string, currentPassword: string = null): Observable<any> {
        return this._httpService.post('changePassword', {
            'currentPassword': currentPassword,
            'newPassword': newPassword
        });
    }

    public logout(): void {
        this.setEnvironmentVariables();
        setDefaultStyles();

        // Salva o email atual antes de limpar localStorage (para preservar tema na próxima visita)
        const lastEmail = this._brandingService.getLastEmail();
        let loggingOutValue = localStorage.getItem(this.loggingOutKey);

        localStorage.clear();
        console.log(`Deleting ${SSO_SUCCESS_KEY} cookie`);
        this._tokenService.removeSsoAuthFromCookie();

        // Restaura o email após clear (mantém tema para próxima visita mesmo deslogado)
        if (lastEmail) {
            this._brandingService.saveLastEmail(lastEmail);
        }

        this._baseAuthService.logout();
        this.isLoginSubject.next(false);
        if (this._platform.is('capacitor')) {
            OAuth2Client.logout(
                getOAuth2Config(this._configService)
            ).then(() => {
                this.router.navigate(['']);
            }).catch(reason => {
                console.error('OAuth logout failed', reason);
            });
        } else if (location && !isNil(this.logoutUrl) && this.logoutUrl !== '') {
            if (loggingOutValue !== 'true') {
                localStorage.setItem(this.loggingOutKey, 'true');
                const novaJanela = window.open(this.logoutUrl, '_blank');
                setTimeout(() => {
                    novaJanela?.close();
                    localStorage.removeItem(this.loggingOutKey);
                }, 1000);
            }
            if (this.logoutByClientEnabled) {
                if (this.hasToken()) {
                    this.router.navigate(['/dashboard']);
                } else {
                    this.router.navigate(['/login']).then(() => {
                        location.reload();
                    });
                }
                return;
            }
            return;
        } else {
            this.router.navigate(['']).then(() => {
                location.reload();
            });
        }
    }

    public getUserData(): Observable<any> {
        const result = this._httpService.get<UserItem>('user');
        return result;
    }

    public refreshUserToken(userId: string): Observable<any> {
        // Usar GET com withCredentials para receber cookies no response
        return this._httpService.get('refreshUserToken', [], [{ name: 'userId', value: userId }], false, true);
    }

    public getIsManagerUser(): Observable<{ manager: boolean }> {
        const result = this._httpService.get<UserItem>('user').pipe(map((response) => ({
            manager: response.data.manager
        })));
        return result;
    }

    public isLoggedIn(): Observable<boolean> {
        return this.isLoginSubject.asObservable();
    }

    public getLoggedUser(): LoginResponse {
        const authData = localStorage.getItem(this.localStorageKey);
        return authData ? JSON.parse(authData) : null;
    }

    public getActiveUser(): string {
        const data = localStorage.getItem('ImpersonatedUser');
        if (data) return (JSON.parse(data)).userId;
        const authData = this.getLoggedUser();
        return authData ? authData.user_id : null;
    }

    public getLoggedUserId(): string {
        const authData = this.getLoggedUser();
        return authData ? authData.user_id : null;
    }

    public getLoggedUserRole(): string {
        const authData = localStorage.getItem(this.localStorageKey);
        return authData ? JSON.parse(authData).role : null;
    }

    public getUserRoles(): Array<string> {
        const user = this.getLoggedUser();
        return user ? user.roles || [] : [];
    }

    public isInRole(role: string): boolean {
        const user = this.getLoggedUser();
        return role && user && user.roles && user.roles.includes(UserRoleEnum[role]);
    }

    public hasRole(role: UserRoleEnum): boolean {
        const user = this.getLoggedUser();
        return role && user && user.roles && user.roles.includes(role);
    }

    public hasRoleImpersonated(role: UserRoleEnum): boolean {
        if (this.isImpersonated()) {
            const user = this.getImpersonatedUser();
            return role && user && user.roles && user.roles.includes(role);
        }
        return this.hasRole(role);
    }

    public isAdmin(): boolean {
        return this.isInRole('Administrator');
    }

    public hasToken() {
        const value = this._tokenStorage.getToken();
        if (value === 'null' || value === 'undefined') return false;

        return !!value;
    }

    public forgotPass(username: string): Observable<any> {
        return this._httpService.post('forgotPassword', { 'username': username });
    }

    public setImpersonationInfo(user: UserPreview): void {
        localStorage.setItem(
            'ImpersonatedUser', JSON.stringify(user)
        );
        this.personificationChanged$.next(true);
    }

    public getImpersonatedUser(): UserPreview {
        const data = localStorage.getItem('ImpersonatedUser');
        return data ? JSON.parse(data) : null;
    }

    public clearImpersonationInfo(): void {
        localStorage.removeItem('ImpersonatedUser');
        this.personificationChanged$.next(false);
    }

    public setPasswordChangeRequired(required: boolean) {
        localStorage.setItem(this.passwordChangeRequiredKey, required === true ? 'true' : 'false');
        this.passwordChangeRequiredSubject.next(required);
    }

    public passwordChangeRequired(): Observable<boolean> {
        return this.passwordChangeRequiredSubject.asObservable();
    }

    public setRedirectUrl(url: string) {
        localStorage.setItem(this.redirectUrlKey, url);
    }

    public getRedirectUrl(): string {
        return localStorage.getItem(this.redirectUrlKey);
    }

    public clearRedirectUrl() {
        localStorage.removeItem(this.redirectUrlKey);
    }

    public isImpersonated() {
        return !!localStorage.getItem('ImpersonatedUser');
    }
}
